import { useEffect, useState, type FormEvent } from "react";
import { ApiError } from "@/lib/api";
import { Button, Dialog, Field, Input } from "@/components/ui/primitives";
import { useCreateVendor, useUpdateVendor } from "./hooks";
import type { Vendor, VendorDetail } from "./api";

const EMPTY = {
  name: "",
  contact_name: "",
  email: "",
  phone: "",
  address: "",
  gst_number: "",
  notes: "",
};

export function VendorFormDialog({
  open,
  onClose,
  editing,
}: {
  open: boolean;
  onClose: () => void;
  editing: Vendor | VendorDetail | null;
}) {
  const create = useCreateVendor();
  const update = useUpdateVendor();
  const [form, setForm] = useState({ ...EMPTY });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      const d = editing as VendorDetail;
      setForm({
        name: d.name,
        contact_name: d.contact_name ?? "",
        email: d.email ?? "",
        phone: d.phone ?? "",
        address: d.address ?? "",
        gst_number: d.gst_number ?? "",
        notes: d.notes ?? "",
      });
    } else {
      setForm({ ...EMPTY });
    }
    setError(null);
  }, [open, editing]);

  const set = (k: keyof typeof EMPTY) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const body = {
      name: form.name.trim(),
      contact_name: form.contact_name.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      address: form.address.trim() || null,
      gst_number: form.gst_number.trim() || null,
      notes: form.notes.trim() || null,
    };
    try {
      if (editing) await update.mutateAsync({ id: editing.id, body });
      else await create.mutateAsync(body);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the vendor.");
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title={editing ? "Edit vendor" : "Add vendor"}>
      <form onSubmit={submit} className="space-y-3">
        {error && (
          <div role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}
        <Field label="Name" htmlFor="v_name">
          <Input id="v_name" value={form.name} onChange={(e) => set("name")(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Contact" htmlFor="v_contact">
            <Input id="v_contact" value={form.contact_name} onChange={(e) => set("contact_name")(e.target.value)} />
          </Field>
          <Field label="Phone" htmlFor="v_phone">
            <Input id="v_phone" value={form.phone} onChange={(e) => set("phone")(e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Email" htmlFor="v_email">
            <Input id="v_email" type="email" value={form.email} onChange={(e) => set("email")(e.target.value)} />
          </Field>
          <Field label="GSTIN" htmlFor="v_gst">
            <Input id="v_gst" value={form.gst_number} onChange={(e) => set("gst_number")(e.target.value)} />
          </Field>
        </div>
        <Field label="Address" htmlFor="v_addr">
          <Input id="v_addr" value={form.address} onChange={(e) => set("address")(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={form.name.trim() === "" || create.isPending || update.isPending}>
            {editing ? "Save changes" : "Create vendor"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
